import { useQuery } from "@tanstack/react-query"
import { format, parseISO } from "date-fns"

import { supabase } from "@/lib/supabase"
import { AUDIT_LOG_SELECT, AuditActor, AuditActorKind } from "@/utils/auditActor"

import type { Tables } from "../../database.types"

export const AUDITED_TABLES = [
  "people",
  "applications",
  "shifts",
  "timesheets",
  "incentives",
  "incentive_tickets",
] as const

export type AuditedTable = (typeof AUDITED_TABLES)[number]

export type AuditLogAction = "insert" | "update" | "delete"

export type AuditLogFieldChange = {
  before: unknown
  after: unknown
}

// `action` and `changes` are text and jsonb in the generated row type, so they
// are narrowed to what the triggers actually write.
export type AuditLog = Omit<Tables<"audit_logs">, "action" | "changes"> &
  AuditActor & {
    action: AuditLogAction
    changes: Record<string, AuditLogFieldChange>
  }

export type AuditLogsFilters = {
  tableName: AuditedTable
  /** A person_id, application_id or raw row UUID. */
  recordReference?: string
  actor?: string
  actorKind?: AuditActorKind | "any"
  action?: AuditLogAction | "any"
  from?: string | null
  to?: string | null
  page?: number
  limit?: number
}

export type AuditLogsResult = {
  logs: AuditLog[]
  hasNextPage: boolean
  page: number
  limit: number
  /** A reference was given but matched no row, so no logs were fetched. */
  unresolvedReference: boolean
  /** Readable names keyed by record_id, for the tables in RECORD_LABELS. */
  recordLabels: Record<string, string>
}

export const AUDIT_LOGS_DEFAULT_PAGE_SIZE = 25

const EMPTY_RESULT = (page: number, limit: number, unresolvedReference: boolean) => ({
  logs: [],
  hasNextPage: false,
  page,
  limit,
  unresolvedReference,
  recordLabels: {},
})

// `actor` is a person label shaped `<person_id>-<Name>`, an edge function's name,
// or `System`. A bare number anchors to the separator, because a contains match would also
// hit those digits inside a name or inside a longer id.
export const actorPattern = (actor: string) => (/^\d+$/.test(actor) ? `${actor}-%` : `%${actor}%`)

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Unescaped, `%` and `_` are ilike wildcards, so a reference containing one
// matches several rows and resolves to an arbitrary one.
export const escapeLikeWildcards = (value: string) => value.replace(/[\\%_]/g, "\\$&")

type FriendlyId = {
  example: string
  findRowId: (reference: string) => Promise<string | null>
}

const firstId = async (
  query: PromiseLike<{ data: { id: string } | null; error: { message: string } | null }>,
) => {
  const { data, error } = await query
  if (error) throw new Error(error.message)
  return data?.id ?? null
}

// An entity absent here is UUID-only. Each entry owns its query because a
// dynamic table and column make the generated types instantiate too deeply.
export const FRIENDLY_IDS: Partial<Record<AuditedTable, FriendlyId>> = {
  people: {
    example: "12345",
    findRowId: (reference) =>
      firstId(
        supabase.from("people").select("id").eq("person_id", reference).limit(1).maybeSingle(),
      ),
  },
  applications: {
    example: "APP-ABC123DEF45",
    // Generated upper-case, matched case-insensitively so a paste still works.
    findRowId: (reference) =>
      firstId(
        supabase
          .from("applications")
          .select("id")
          .ilike("application_id", escapeLikeWildcards(reference))
          .limit(1)
          .maybeSingle(),
      ),
  },
}

// Read from the record's own audit rows rather than its table, so a label survives the row
// being deleted and shows to an Admin whom that table's RLS would refuse.
const latestAuditedFields = async (table: AuditedTable, recordIds: string[], fields: string[]) => {
  const { data, error } = await supabase
    .from("audit_logs")
    .select("record_id, action, changes")
    .eq("table_name", table)
    .in("record_id", recordIds)
    .or(fields.map((field) => `changes->${field}.not.is.null`).join(","))
    .order("created_at", { ascending: false })
  if (error) throw new Error(error.message)

  const latest = new Map<string, Record<string, unknown>>()
  for (const row of data ?? []) {
    const changes = row.changes as AuditLog["changes"]
    const values = latest.get(row.record_id) ?? {}
    for (const field of fields) {
      if (field in values || !changes?.[field]) continue
      values[field] = row.action === "delete" ? changes[field].before : changes[field].after
    }
    latest.set(row.record_id, values)
  }
  return latest
}

const incentiveNames = async (recordIds: string[]) => {
  const incentives = await latestAuditedFields("incentives", recordIds, ["name"])
  const names = new Map<string, string>()
  for (const [id, { name }] of incentives) {
    if (typeof name === "string") names.set(id, name)
  }
  return names
}

type RecordLabeler = (recordIds: string[]) => Promise<Map<string, string>>

// An entity absent here shows its row UUID alone.
export const RECORD_LABELS: Partial<Record<AuditedTable, RecordLabeler>> = {
  incentives: incentiveNames,
  incentive_tickets: async (recordIds) => {
    const tickets = await latestAuditedFields("incentive_tickets", recordIds, [
      "incentive_id",
      "period_start",
    ])
    const incentiveIds = [...tickets.values()].map(({ incentive_id }) => String(incentive_id))
    const names = await incentiveNames([...new Set(incentiveIds)])

    const labels = new Map<string, string>()
    for (const [id, { incentive_id, period_start }] of tickets) {
      const name = names.get(String(incentive_id)) ?? "Unknown incentive"
      labels.set(
        id,
        period_start
          ? `${name} · from ${format(parseISO(String(period_start)), "d MMM yyyy")}`
          : name,
      )
    }
    return labels
  },
}

const labelRecords = async (tableName: AuditedTable, logs: AuditLog[]) => {
  const labeler = RECORD_LABELS[tableName]
  if (!labeler || logs.length === 0) return {}

  const labels = await labeler([...new Set(logs.map((log) => log.record_id))])
  return Object.fromEntries(labels)
}

// `record_id` holds the row UUID, but an operator searches by a friendly id.
// A null return means no match, which the caller must treat as "no rows".
const resolveRecordId = async (tableName: AuditedTable, reference: string) => {
  if (UUID_PATTERN.test(reference)) {
    return reference
  }

  return FRIENDLY_IDS[tableName]?.findRowId(reference) ?? null
}

export function useAuditLogs(filters: AuditLogsFilters) {
  return useQuery<AuditLogsResult>({
    queryKey: ["auditLogs", filters],
    placeholderData: (previousData) => previousData,
    queryFn: async () => {
      const page = filters.page ?? 1
      const limit = filters.limit ?? AUDIT_LOGS_DEFAULT_PAGE_SIZE
      const fromIndex = (page - 1) * limit
      // One row past the page tells us whether there is a next one.
      const toIndex = fromIndex + limit

      const reference = filters.recordReference?.trim()
      const recordId = reference ? await resolveRecordId(filters.tableName, reference) : null

      // Dropping the filter instead would widen the search to the whole table.
      if (reference && !recordId) {
        return EMPTY_RESULT(page, limit, true)
      }

      let query = supabase
        .from("audit_logs")
        .select(AUDIT_LOG_SELECT)
        .eq("table_name", filters.tableName)
        .order("created_at", { ascending: false })
        .range(fromIndex, toIndex)

      if (recordId) {
        query = query.eq("record_id", recordId)
      }

      const actor = filters.actor?.trim()
      if (actor) {
        query = query.ilike("actor", actorPattern(actor))
      }

      if (filters.actorKind && filters.actorKind !== "any") {
        query = query.eq("actor_kind", filters.actorKind)
      }

      if (filters.action && filters.action !== "any") {
        query = query.eq("action", filters.action)
      }

      if (filters.from) {
        query = query.gte("created_at", filters.from)
      }

      if (filters.to) {
        query = query.lte("created_at", filters.to)
      }

      const { data, error } = await query
      if (error) throw new Error(error.message)

      const rows = (data as unknown as AuditLog[]) ?? []
      const logs = rows.slice(0, limit)

      return {
        logs,
        hasNextPage: rows.length > limit,
        page,
        limit,
        unresolvedReference: false,
        recordLabels: await labelRecords(filters.tableName, logs),
      }
    },
  })
}
